import { Select as SelectPrimitive } from '@base-ui/react/select'
import { Combobox as ComboboxPrimitive } from '@base-ui/react/combobox'
import { Checkbox as CheckboxPrimitive } from '@base-ui/react/checkbox'
import { Switch as SwitchPrimitive } from '@base-ui/react/switch'
import { Label } from '@betstarters/ui'
import { Check, ChevronsUpDown } from 'lucide-react'
import { useMemo, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

/**
 * Form controls the backoffice needs and @betstarters/ui does not ship: a
 * select, a checkbox and a switch.
 *
 * They wrap @base-ui/react — the same primitive layer the kit itself is built
 * on — and reuse the kit's tokens (border-input, ring-ring, bg-background,
 * muted…), so they sit next to Input, Button and Badge without looking like a
 * different product. Nothing here is hand-rolled HTML with ad-hoc styling.
 *
 * When to use which boolean control:
 *   Checkbox — a field inside a form you submit (the dialogs).
 *   Switch   — a setting that takes effect on the spot (the table toggles).
 *
 * And which picker:
 *   Select   — a short, memorable list (game types, movement types).
 *   Combobox — anything you would have to scroll: countries (245), wallet
 *              causals (179), setting types (454). Scrolling a 454-item popup
 *              is not a picker, it is a haystack.
 */

/** Lists longer than this are unusable without a search box. */
export const COMBOBOX_THRESHOLD = 25

export interface SelectItem {
  value: string
  label: string
}

export function Select({
  value,
  onValueChange,
  items,
  placeholder,
  className = '',
  disabled,
}: {
  value: string
  onValueChange: (value: string) => void
  items: SelectItem[]
  /** Shown when nothing is selected; selecting it clears the value. */
  placeholder?: string
  className?: string
  disabled?: boolean
}) {
  // Base UI resolves the trigger's label from this map, so Select.Value renders
  // the chosen item's text without the caller wiring it up.
  const labels: Record<string, ReactNode> = {}
  for (const item of items) labels[item.value] = item.label

  const options: SelectItem[] = placeholder ? [{ value: '', label: placeholder }, ...items] : items

  return (
    <SelectPrimitive.Root
      value={value}
      onValueChange={(next) => onValueChange(next == null ? '' : String(next))}
      items={labels}
      disabled={disabled}
    >
      <SelectPrimitive.Trigger
        className={`flex h-8 min-w-0 items-center justify-between gap-2 rounded-lg border border-input bg-transparent px-2.5 py-1 text-sm transition-colors outline-none select-none hover:bg-muted/50 focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 data-[popup-open]:bg-muted/50 dark:bg-input/30 ${className}`}
      >
        <SelectPrimitive.Value placeholder={placeholder ?? ''} className="truncate" />
        <SelectPrimitive.Icon className="shrink-0 text-muted-foreground">
          <ChevronsUpDown className="size-3.5" />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>

      <SelectPrimitive.Portal>
        <SelectPrimitive.Positioner sideOffset={4} className="z-50">
          <SelectPrimitive.Popup className="max-h-80 min-w-[var(--anchor-width)] overflow-y-auto rounded-lg bg-background p-1 text-sm ring-1 ring-foreground/10 outline-none data-open:animate-in data-open:fade-in-0 data-closed:animate-out data-closed:fade-out-0">
            {options.map((item) => (
              <SelectPrimitive.Item
                key={item.value || '__placeholder'}
                value={item.value}
                className="flex cursor-default items-center justify-between gap-2 rounded-md px-2 py-1.5 outline-none select-none data-[highlighted]:bg-muted data-[highlighted]:text-foreground"
              >
                <SelectPrimitive.ItemText className="truncate">{item.label}</SelectPrimitive.ItemText>
                <SelectPrimitive.ItemIndicator className="shrink-0 text-primary">
                  <Check className="size-3.5" />
                </SelectPrimitive.ItemIndicator>
              </SelectPrimitive.Item>
            ))}
          </SelectPrimitive.Popup>
        </SelectPrimitive.Positioner>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  )
}

/** Checkbox with its label — a form field, saved when the form is submitted. */
export function Checkbox({
  id,
  checked,
  onCheckedChange,
  label,
  disabled,
}: {
  id: string
  checked: boolean
  onCheckedChange: (checked: boolean) => void
  label: ReactNode
  disabled?: boolean
}) {
  return (
    <div className="flex items-center gap-2">
      <CheckboxPrimitive.Root
        id={id}
        checked={checked}
        onCheckedChange={onCheckedChange}
        disabled={disabled}
        className="flex size-4 items-center justify-center rounded-[4px] border border-input bg-transparent outline-none transition-colors focus-visible:ring-3 focus-visible:ring-ring/50 disabled:opacity-50 data-[checked]:border-primary data-[checked]:bg-primary data-[checked]:text-primary-foreground"
      >
        <CheckboxPrimitive.Indicator className="flex">
          <Check className="size-3" />
        </CheckboxPrimitive.Indicator>
      </CheckboxPrimitive.Root>
      <Label htmlFor={id} className="cursor-pointer text-sm font-normal">
        {label}
      </Label>
    </div>
  )
}

/** Switch — a setting that takes effect the moment it is flipped. */
export function Switch({
  checked,
  onCheckedChange,
  label,
  disabled,
}: {
  checked: boolean
  onCheckedChange: (checked: boolean) => void
  /** Read out next to the switch; also its accessible name. */
  label: string
  disabled?: boolean
}) {
  return (
    <div className="flex items-center gap-2">
      <SwitchPrimitive.Root
        checked={checked}
        onCheckedChange={onCheckedChange}
        disabled={disabled}
        aria-label={label}
        className="relative h-5 w-9 shrink-0 rounded-full bg-muted transition-colors outline-none focus-visible:ring-3 focus-visible:ring-ring/50 disabled:opacity-50 data-[checked]:bg-primary"
      >
        <SwitchPrimitive.Thumb className="block size-4 translate-x-0.5 rounded-full bg-background shadow transition-transform data-[checked]:translate-x-[1.125rem]" />
      </SwitchPrimitive.Root>
      <span className="text-sm text-muted-foreground">{label}</span>
    </div>
  )
}

export function Combobox({
  value,
  onValueChange,
  items,
  placeholder,
  className = '',
  disabled,
}: {
  value: string
  onValueChange: (value: string) => void
  items: SelectItem[]
  /** Shown in the input when nothing is selected. */
  placeholder?: string
  className?: string
  disabled?: boolean
}) {
  const { t } = useTranslation('app')
  const [query, setQuery] = useState('')

  const selected = items.find((item) => item.value === value) ?? null

  // Filtering is ours rather than the primitive's: the lists are already in
  // memory (the reference lookups are fetched whole and cached), so a plain
  // case-insensitive contains is both enough and predictable.
  const filtered = useMemo(() => {
    const term = query.trim().toLowerCase()
    return term ? items.filter((item) => item.label.toLowerCase().includes(term)) : items
  }, [items, query])

  return (
    <ComboboxPrimitive.Root
      items={filtered}
      value={selected}
      onValueChange={(item: SelectItem | null) => onValueChange(item ? item.value : '')}
      itemToStringLabel={(item: SelectItem) => item.label}
      onInputValueChange={setQuery}
      onOpenChange={(open) => {
        if (!open) setQuery('')
      }}
      disabled={disabled}
    >
      <div className={`relative ${className}`}>
        <ComboboxPrimitive.Input
          placeholder={placeholder}
          className="h-8 w-full min-w-0 rounded-lg border border-input bg-transparent py-1 pr-7 pl-2.5 text-sm transition-colors outline-none placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 dark:bg-input/30"
        />
        <ComboboxPrimitive.Trigger
          className="absolute top-1/2 right-2 -translate-y-1/2 text-muted-foreground outline-none"
          aria-label={placeholder}
        >
          <ChevronsUpDown className="size-3.5" />
        </ComboboxPrimitive.Trigger>
      </div>

      <ComboboxPrimitive.Portal>
        <ComboboxPrimitive.Positioner sideOffset={4} className="z-50">
          <ComboboxPrimitive.Popup className="max-h-80 w-[var(--anchor-width)] overflow-y-auto rounded-lg bg-background p-1 text-sm ring-1 ring-foreground/10 outline-none data-open:animate-in data-open:fade-in-0 data-closed:animate-out data-closed:fade-out-0">
            <ComboboxPrimitive.Empty className="px-2 py-3 text-center text-muted-foreground">
              {t('common.noResults')}
            </ComboboxPrimitive.Empty>
            <ComboboxPrimitive.List>
              {(item: SelectItem) => (
                <ComboboxPrimitive.Item
                  key={item.value}
                  value={item}
                  className="flex cursor-default items-center justify-between gap-2 rounded-md px-2 py-1.5 outline-none select-none data-[highlighted]:bg-muted data-[highlighted]:text-foreground"
                >
                  <span className="truncate">{item.label}</span>
                  <ComboboxPrimitive.ItemIndicator className="shrink-0 text-primary">
                    <Check className="size-3.5" />
                  </ComboboxPrimitive.ItemIndicator>
                </ComboboxPrimitive.Item>
              )}
            </ComboboxPrimitive.List>
          </ComboboxPrimitive.Popup>
        </ComboboxPrimitive.Positioner>
      </ComboboxPrimitive.Portal>
    </ComboboxPrimitive.Root>
  )
}
